import React, { useState } from 'react'

import { Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import * as yaml from 'js-yaml'

import RecommendationCard from '$extensionSrc/components/Sidebar/RecommendationCards/RecommendationCardContainer/RecommendationCard'
import RecommendationDisplayWrapper from '$extensionSrc/recommendations/recommendationDisplayWrapper'
import { parseSpec } from '$extensionSrc/recommendations/recommendationSpecParsing'
import {
  Recommendation,
  RecommendationKeyEnum,
} from '$extensionSrc/recommendations/recommendationTypes'
import {
  ConcernTag,
  PLATFORM,
  RecommendationState,
} from '$extensionSrc/utils/enums'

import './RecommendationCardPreview.scss'

const CONCERN_TAG_OPTIONS = [
  ConcernTag.ACCOUNT_SECURITY,
  ConcernTag.ACCOUNT_SEPARATION,
  ConcernTag.CHILD_SAFETY,
  ConcernTag.DATA_MISUSE,
  ConcernTag.DIGITAL_WELLNESS,
  ConcernTag.ONLINE_ABUSE,
  ConcernTag.PHYSICAL_SAFETY,
]

export default function RecommendationCardPreview() {
  const [concernTag, setConcernTag] = useState<ConcernTag>(
    ConcernTag.ACCOUNT_SECURITY,
  )
  const [yamlContent, setYamlContent] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [specs, setSpecs] = useState<Record<string, Recommendation>>({})

  const handlePreview = (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault()
    try {
      setError(null)
      // Parse YAML content
      const parsedYaml = yamlContent ? yaml.load(yamlContent) : {}
      setSpecs(parsedYaml as Record<string, Recommendation>)
    } catch (err) {
      setError(
        `Error parsing YAML: ${err instanceof Error ? err.message : 'Unknown error'}`,
      )
      setSpecs({})
    }
  }
  const renderPreviewContent = () => {
    if (error) {
      return <div className="error-message">{error}</div>
    }

    if (Object.keys(specs).length === 0) {
      return <div className="empty-preview">No YAML content to preview</div>
    }

    return Object.entries(specs).map(([recommendationKey, spec]) => {
      let parsedSpec = {} as Recommendation

      let settingsDef
      if (spec.isAutomated) {
        settingsDef = {
          getCantUndoFn: () => () => false,
          getCurrentValueFn: () => () => 'value',
          getIsDisabledFn: () => () => false,
          getIsDisabledReasonFn: () => () => null,
          getIsFulfilledFn: () => () => false,
          gatherStageDefs: [],
          updateStageDefs: [],
          undoStageDefs: [],
        }
      }

      parsedSpec = {
        ...parseSpec(spec),
        key: recommendationKey as RecommendationKeyEnum,
      }

      const progress = {
        initialState: {},
        automationErrors: null,
        recommendationState: RecommendationState.INCOMPLETE,
        lastGatheredTimestamp: null,
        lastUpdatedTimestamp: -1,
        settingsSelected: parsedSpec.settings
          ? Object.fromEntries(
              parsedSpec.settings.map((setting) => [setting.key, true]),
            )
          : {},
      }

      return (
        <div className="recommendation-card-container" key={recommendationKey}>
          <RecommendationCard
            accessLevel={undefined}
            automationDef={settingsDef}
            displayPosition={0}
            isLanguageSupported
            isLessRelevant={false}
            isPlaybookCompleted={false}
            lastDeeplinkFeatureKey={null}
            platform={PLATFORM.TWITTER}
            progress={progress}
            recommendationKey={recommendationKey}
            onAnimatedTransition={() => {}}
            onAutomationStarted={() => {}}
            showUpsellModal={() => {}}
            spec={
              new RecommendationDisplayWrapper(
                parsedSpec,
                concernTag || parsedSpec.concernTags[0],
              )
            }
            userId="test-user-id"
          />
        </div>
      )
    })
  }

  return (
    <div className="RecommendationCardPreview">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <form className="form-section">
            <div className="form-field">
              <InputLabel id="concern-tag">Primary Concern Tag:</InputLabel>
              <Select
                labelId="concern-tag"
                value={concernTag}
                onChange={(e) => setConcernTag(e.target.value as ConcernTag)}
                fullWidth
              >
                {CONCERN_TAG_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="form-field">
              <InputLabel id="yaml-content">YAML:</InputLabel>
              <TextField
                slotProps={{
                  inputLabel: {
                    htmlFor: 'yaml-content',
                  },
                }}
                multiline
                rows={20}
                value={yamlContent}
                onChange={(e) => setYamlContent(e.target.value)}
                placeholder="Enter YAML content here..."
                fullWidth
                variant="outlined"
              />
            </div>

            <button type="submit" onClick={handlePreview}>
              Preview
            </button>
          </form>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <div className="preview-section">
            <h3>Preview</h3>
            <div className="preview-content">{renderPreviewContent()}</div>
          </div>
        </Grid>
      </Grid>
    </div>
  )
}
